import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  inject,
  computed,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Router, NavigationEnd } from '@angular/router';
import { Subject, takeUntil, filter } from 'rxjs';

import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  OrganizationService,
  SearchFormData,
} from '../services/organization.service';
import { PermissionService } from '@app/core/services/permission.service';
import {
  CompanyItem,
  COMPANY_SEARCH_CONFIG,
  COMPANY_COLUMNS,
  COMPANY_DEFAULT_COLUMNS_ORDER,
  COMPANY_FLEXIBLE_COLUMNS_ORDER,
  COMPANY_ACTION_COLUMN,
  getStatusSeverity,
  getStatusDisplayText,
  getStatusStyle,
} from './company.config';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import {
  ActionButton,
  StatusFilterComponent,
} from '@app/shared/components/status-filter/status-filter.component';
import {
  CompanyDialogMode,
  CompanyFormData,
  CompanyFormDialogComponent,
} from '../components/company-form-dialog/company-form-dialog';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { HttpErrorResponse } from '@angular/common/http';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';

@Component({
  selector: 'app-company',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    AdvancedDataTableComponent,
    AppButtonComponent,
    SearchFormComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService, DialogService],
  templateUrl: './company.component.html',
  styleUrl: './company.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyComponent implements OnInit, OnDestroy {
  @ViewChild('advancedTable')
  advancedTable?: AdvancedDataTableComponent;

  // Configuration
  searchFormConfig = COMPANY_SEARCH_CONFIG;

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      COMPANY_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of COMPANY_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(COMPANY_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      COMPANY_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of COMPANY_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Check if user has any action permissions (UPDATE, APPROVE, or DELETE)
  hasAnyActionPermission = computed(
    () =>
      this.canUpdate() ||
      this.canApprove() ||
      this.canDelete()
  );

  // Data signals
  companies = signal<CompanyItem[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Search parameters
  searchParams = signal({
    code: '',
    name: '',
    status: '',
    page: 0,
    size: 20,
  });

  // Button configuration base
  private readonly primaryButtonBase: ActionButton = {
    label: 'common.actions.new',
    severity: 'info',
    show: true,
    icon: 'add',
  };

  // Confirmation dialog state
  showConfirmationDialog = signal(false);
  confirmationTitle = signal('');
  confirmationMessage = signal('');
  confirmationButtons = signal<ConfirmationButton[]>([]);
  selectedCompanyForAction = signal<CompanyItem | null>(null);
  currentAction = signal<'delete' | 'status' | null>(null);
  confirmationActionLoading = signal(false);

  // Private properties
  private readonly destroy$ = new Subject<void>();
  private readonly translateService = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private readonly dialogService = inject(DialogService);
  private readonly dialogRef: DynamicDialogRef | null = null;
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);

  // Permission flags - computed from PermissionService
  // Using menu codes: settings.user.company_management.view/create/edit/delete/approve
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.user.company_management.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.user.company_management.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.user.company_management.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.user.company_management.delete');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.user.company_management.approve');
  });

  primaryButton = computed(() => ({
    ...this.primaryButtonBase,
    show: this.canCreate(),
  }));

  constructor(
    private readonly router: Router,
    private readonly organizationService: OrganizationService,
    private readonly messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadCompanies();
    this.setupRouterListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Load Companies
  private loadCompanies(): void {
    this.loading.set(true);
    this.organizationService.searchCompanies(this.searchParams()).subscribe({
      next: (response) => {
        this.companies.set(response.data);
        this.totalRecords.set(response.totalRecords);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading Companies:', error);
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'company.messages.error.failed_to_load_companies'
          ),
        });
      },
    });
  }

  private setupRouterListener(): void {
    // Listen for navigation events to refresh data when returning to this page
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        // Check if we're navigating to the company list page
        if (event.url.includes('/configuration/user/company')) {
          // Refresh data when returning to list page
          this.loadCompanies();
        }
      });
  }

  onSearch(searchData: SearchFormData): void {
    this.searchParams.set({ ...this.searchParams(), ...searchData, page: 0 });
    this.loadCompanies();
  }

  onReset(): void {
    this.searchParams.set({
      code: '',
      name: '',
      status: '',
      page: 0,
      size: 20,
    });
    this.loadCompanies();
  }

  onPageChange(event: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      page: event.page,
      size: event.rows,
    });
    this.loadCompanies();
  }

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Find the actual button element from PrimeNG p-button
    // p-button wraps a button element, so we need to find it
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }

  // Status methods
  getStatusSeverity(
    status: string
  ): 'success' | 'warning' | 'danger' | 'secondary' {
    return getStatusSeverity(status);
  }

  getStatusDisplayText(status: string): string {
    return getStatusDisplayText(status, this.translateService);
  }

  getStatusStyle(status: string): any {
    return getStatusStyle(status);
  }

  // Date formatting using formatDateTime utility
  formatDateTime(dateInput: any): string {
    return formatDateTime(dateInput);
  }

  // View Company details
  onView(company: CompanyItem): void {
    if (!this.canRead()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission'
        ),
      });
      return;
    }

    this.loadCompanyAndOpenDialog(company.id, 'view');
  }

  onAdd(): void {
    if (!this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission'
        ),
      });
      return;
    }

    this.dialogService.open(CompanyFormDialogComponent, {
      header: this.translateService.instant(
        'organizational.company.form.title_add'
      ),
      width: '480px',
      modal: true,
      dismissableMask: false,
      data: {
        mode: 'create',
        onSubmit: ({
          data,
          onSuccess,
          onError,
        }: {
          data: CompanyFormData;
          onSuccess: () => void;
          onError: (message: string) => void;
        }) => {
          this.handleCreateCompany(data, onSuccess, onError);
        },
      },
    });
  }

  // Edit Company
  onEdit(company: CompanyItem): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission'
        ),
      });
      return;
    }

    this.loadCompanyAndOpenDialog(company.id, 'edit');
  }

  // Delete Company
  onDelete(company: CompanyItem): void {
    if (!this.canDelete()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission'
        ),
      });
      return;
    }

    this.selectedCompanyForAction.set(company);
    this.currentAction.set('delete');
    this.confirmationTitle.set(
      this.translateService.instant('common.confirmation.title')
    );
    this.confirmationMessage.set(
      this.translateService.instant(
        'organizational.company.messages.confirmation.delete',
        { name: company.name }
      )
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        action: 'confirm',
        severity: 'danger',
      },
    ]);
    this.showConfirmationDialog.set(true);
  }

  onStatusChange(company: CompanyItem, newStatus: string): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission'
        ),
      });
      return;
    }

    const isActivating = newStatus === 'ACTIVE';
    this.selectedCompanyForAction.set(company);
    this.currentAction.set('status');
    this.confirmationTitle.set(
      this.translateService.instant('common.confirmation.title')
    );
    this.confirmationMessage.set(
      this.translateService.instant(
        isActivating
          ? 'organizational.company.messages.confirmation.activate'
          : 'organizational.company.messages.confirmation.deactivate',
        { name: company.name }
      )
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant(
          isActivating
            ? 'common.table.actions.activate'
            : 'common.table.actions.deactivate'
        ),
        action: 'confirm',
        severity: 'info',
      },
    ]);
    this.showConfirmationDialog.set(true);
  }

  private handleCreateCompany(
    data: CompanyFormData,
    onSuccess: () => void,
    onError: (message: string) => void
  ): void {
    const payload: Partial<CompanyItem> & {
      legalPerson?: string;
      businessLicense?: string;
      shopPermitName?: string;
    } = {
      code: data.code,
      name: data.name,
      shopPermitName: data.registeredBusinessName,
      legalPerson: data.legalPerson,
      businessLicense: data.businessLicense,
      status: data.status ?? 'ACTIVE',
    };

    this.organizationService.createCompany(payload).subscribe({
      next: () => {
        onSuccess();

        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant(
            'organizational.company.messages.success.company_created'
          ),
        });

        this.loadCompanies();
      },
      error: (error: HttpErrorResponse) => {
        this.extractErrorMessage(error).then(({ message: errorMessage }) => {
          onError(
            this.resolveCompanyErrorMessage(
              errorMessage,
              data.code,
              'organizational.company.messages.error.failed_to_create_company'
            )
          );
        });
      },
    });
  }

  private loadCompanyAndOpenDialog(
    companyId: string,
    mode: CompanyDialogMode
  ): void {
    this.showLoadingToast();

    this.organizationService.getCompanyById(companyId).subscribe({
      next: (company) => {
        this.clearLoadingToast();

        const companyFormData: CompanyFormData = {
          id: company.id,
          code: company.code,
          name: company.name,
          status: company.status as 'ACTIVE' | 'INACTIVE',
          registeredBusinessName: company.shopPermitName,
          legalPerson: company.legalPerson,
          businessLicense: company.businessLicense,
        };

        this.dialogService.open(CompanyFormDialogComponent, {
          header: this.translateService.instant(
            mode === 'view'
              ? 'organizational.company.form.title_view'
              : 'organizational.company.form.title_edit'
          ),
          width: '480px',
          modal: true,
          dismissableMask: false,
          closeOnEscape: true,
          draggable: false,
          resizable: false,
          styleClass: 'company-form-dialog-wrapper',
          data: {
            mode,
            company: companyFormData,
            onSubmit:
              mode === 'edit'
                ? ({
                    data,
                    onSuccess,
                    onError,
                  }: {
                    data: CompanyFormData;
                    onSuccess: () => void;
                    onError: (message: string) => void;
                  }) => {
                    this.handleUpdateCompany(
                      company.id,
                      data,
                      onSuccess,
                      onError
                    );
                  }
                : undefined,
          },
        });
      },
      error: () => {
        this.clearLoadingToast();
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'organizational.company.messages.error.failed_to_load_company'
          ),
        });
      },
    });
  }

  private showLoadingToast(): void {
    this.messageService.add({
      key: 'company-loading',
      severity: 'info',
      summary: this.translateService.instant('common.messages.loading'),
      detail: this.translateService.instant(
        'organizational.company.messages.info.loading_detail'
      ),
      life: 0, // stay until cleared
    });
  }

  private clearLoadingToast(): void {
    this.messageService.clear('company-loading');
  }

  private handleUpdateCompany(
    companyId: string,
    data: CompanyFormData,
    onSuccess: () => void,
    onError: (message: string) => void
  ): void {
    const payload: Partial<CompanyItem> & {
      legalPerson?: string;
      businessLicense?: string;
      shopPermitName?: string;
    } = {
      code: data.code,
      name: data.name,
      shopPermitName: data.registeredBusinessName,
      legalPerson: data.legalPerson,
      businessLicense: data.businessLicense,
      status: data.status ?? 'ACTIVE',
    };

    this.organizationService.updateCompany(companyId, payload).subscribe({
      next: () => {
        onSuccess();

        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant(
            'organizational.company.messages.success.company_updated'
          ),
        });

        this.loadCompanies();
      },
      error: (error: HttpErrorResponse) => {
        this.extractErrorMessage(error).then(({ message: errorMessage }) => {
          onError(
            this.resolveCompanyErrorMessage(
              errorMessage,
              data.code,
              'organizational.company.messages.error.failed_to_update_company'
            )
          );
        });
      },
    });
  }

  onConfirmationDialogAction(action: string): void {
    if (action === 'cancel') {
      this.closeConfirmationDialog();
      return;
    }

    if (action === 'confirm') {
      const company = this.selectedCompanyForAction();
      const actionType = this.currentAction();

      if (!company || !actionType) {
        this.closeConfirmationDialog();
        return;
      }

      // Set loading state on confirm button
      this.confirmationActionLoading.set(true);
      this.updateConfirmationButtonsLoading(true);

      if (actionType === 'delete') {
        this.handleDeleteCompany(company);
      } else if (actionType === 'status') {
        const newStatus = company.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        this.handleStatusChange(company, newStatus);
      }
    }
  }

  private updateConfirmationButtonsLoading(loading: boolean): void {
    const buttons = this.confirmationButtons().map((button) => {
      if (button.action === 'confirm') {
        return { ...button, loading, disabled: loading };
      }
      return { ...button, disabled: loading };
    });
    this.confirmationButtons.set(buttons);
  }

  /**
   * Extract error message from HttpErrorResponse
   * Handles both Blob and JSON error responses
   * Returns both the error message and the full parsed error object
   */
  private async extractErrorMessage(
    error: HttpErrorResponse
  ): Promise<{ message: string; parsedError?: any }> {
    // Handle Blob error response - convert to JSON
    if (error?.error instanceof Blob) {
      try {
        const parsedError = await BlobConverterUtil.convertToJson(error.error);
        return {
          message: parsedError?.message || '',
          parsedError: parsedError,
        };
      } catch (parseError) {
        console.error('Error parsing blob error response:', parseError);
        return { message: error?.message || '' };
      }
    }

    // Direct JSON error response
    const errorMessage = error?.error?.message || error?.message || '';
    return { message: errorMessage, parsedError: error?.error };
  }

  // Backend has no dedicated error code, so we match on message text
  private resolveCompanyErrorMessage(
    errorMessage: string,
    code: string,
    fallbackKey: string
  ): string {
    const isDuplicateCodeError = errorMessage
      .toLowerCase()
      .includes('already exists');

    if (isDuplicateCodeError) {
      return this.translateService.instant(
        'organizational.company.messages.error.code_already_exists',
        { code }
      );
    }

    return this.translateService.instant(fallbackKey);
  }

  private handleDeleteCompany(company: CompanyItem): void {
    this.loading.set(true);

    this.organizationService.deleteCompany(company.id).subscribe({
      next: () => {
        this.loading.set(false);
        this.confirmationActionLoading.set(false);
        this.updateConfirmationButtonsLoading(false);
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant(
            'organizational.company.messages.success.company_deleted'
          ),
        });

        // Reset to first page if current page becomes empty
        const currentPage = this.searchParams().page;
        if (this.companies().length === 1 && currentPage > 0) {
          this.searchParams.set({
            ...this.searchParams(),
            page: Math.max(0, currentPage - 1),
          });
        }

        this.closeConfirmationDialog();
        this.loadCompanies();
      },
      error: (error: HttpErrorResponse) => {
        this.closeConfirmationDialog();
        this.loading.set(false);

        // Extract error message (handles Blob and JSON)
        this.extractErrorMessage(error).then(
          ({ message: errorMessage, parsedError }) => {
            // Check if error message includes the specific text about existing shops
            const hasExistingShopsError = errorMessage
              .toLowerCase()
              .includes('cannot delete sales organization with existing shops');

            let translatedMessage: string;

            if (hasExistingShopsError) {
              // Show the specific translated message for this error
              translatedMessage = this.translateService.instant(
                'organizational.company.messages.error.cannot_delete_with_shops'
              );
            } else {
              // For other errors, show generic delete_failed message
              translatedMessage = this.translateService.instant(
                'organizational.company.messages.error.delete_failed'
              );
            }

            // Show error dialog instead of toast
            this.errorDialogService.showError(translatedMessage);
          }
        );
      },
    });
  }

  private handleStatusChange(company: CompanyItem, newStatus: string): void {
    this.loading.set(true);

    const payload: Partial<CompanyItem> = {
      code: company.code,
      name: company.name,
      status: newStatus,
    };

    this.organizationService.updateCompany(company.id, payload).subscribe({
      next: () => {
        this.loading.set(false);
        this.confirmationActionLoading.set(false);
        this.updateConfirmationButtonsLoading(false);
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant(
            newStatus === 'ACTIVE'
              ? 'organizational.company.messages.success.company_activated'
              : 'organizational.company.messages.success.company_deactivated'
          ),
        });

        this.closeConfirmationDialog();
        this.loadCompanies();
      },
      error: (error) => {
        this.loading.set(false);
        this.confirmationActionLoading.set(false);
        this.updateConfirmationButtonsLoading(false);
        console.error('Error updating company status:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'organizational.company.messages.error.failed_to_update_status'
          ),
        });
      },
    });
  }

  private closeConfirmationDialog(): void {
    this.showConfirmationDialog.set(false);
    this.selectedCompanyForAction.set(null);
    this.currentAction.set(null);
    this.confirmationActionLoading.set(false);
  }
}
